import { supabase } from "@/lib/supabase";
import { getAllDocuments, type DocumentListItem } from "@/lib/documents";
import { requireAccountId } from "@/lib/profile";
import { agingBucketOf, daysLate, isOpenStatus, isReceivable, type AgingBucketKey } from "@/lib/docStatus";
import type { ClientRow, DueTermId } from "@/lib/types";

/**
 * Clients-first architecture (backlog J14, migration 0005): a real
 * `clients` table now exists, but every document created before this
 * shipped only ever carried clientName/clientAddress/clientPhone/
 * clientEmail as plain strings — so this stays a *coexistence* model, not
 * a cutover. A client here is either:
 *   - a real row from the `clients` table (`isReal: true`), matched to
 *     documents by `doc.clientId` first, falling back to a normalized
 *     name match (`slugifyClientName`) so pre-existing documents for a
 *     client the sub is now formalizing don't get orphaned; or
 *   - a "legacy" pseudo-client (`isReal: false`) derived purely by
 *     grouping documents on clientName, exactly like before this file
 *     existed — for any document that doesn't match a real client either
 *     way. Nothing that showed up in the directory before this shipped
 *     disappears.
 * Deliberately no backfill: a document that matches a real client only by
 * name keeps matching by name indefinitely, unless it's re-saved through
 * a builder with that client explicitly picked (which sets clientId).
 */
export interface ClientSummary {
  id: string;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  city: string;
  address: string;
  notes: string;
  term: DueTermId;
  retainagePct: number;
  /** Hand-edited sales tax %, remembered per client (migration 0009) — replaces the state-base default (@/lib/salesTax) on this client's future quotes until cleared. Null when no override has been set. */
  taxOverridePct: number | null;
  /** Hand-edited materials markup %, remembered per client (migration 0010) — replaces the account default markup on this client's future quotes until cleared. Null when no override has been set. */
  markupOverridePct: number | null;
  outstanding: number;
  openDocs: number;
  totalDocs: number;
  docs: DocumentListItem[];
  /** True for a real `clients` table row; false for a legacy derived-from-documents pseudo-client. */
  isReal: boolean;
}

export interface ClientDraft {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  city: string;
  address: string;
  notes: string;
  term: DueTermId;
  retainagePct: number;
  /** Tags the row for sampleData.ts's clearSampleData() to find and remove. Defaults to false. */
  sample?: boolean;
}

export const RETAINAGE_OPTIONS = [0, 5, 10] as const;
export const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Fields a real client row can be patched on (backlog J16 — editing via ClientFormPage). */
export type ClientEditableField =
  | "name"
  | "contactName"
  | "email"
  | "phone"
  | "city"
  | "address"
  | "notes"
  | "term"
  | "retainagePct"
  | "taxOverridePct"
  | "markupOverridePct";
export type ClientPatch = Partial<Pick<ClientSummary, ClientEditableField>>;

const FIELD_COLUMN: Record<ClientEditableField, string> = {
  name: "name",
  contactName: "contact_name",
  email: "email",
  phone: "phone",
  city: "city",
  address: "address",
  notes: "notes",
  term: "term",
  retainagePct: "retainage_pct",
  taxOverridePct: "tax_override_pct",
  markupOverridePct: "markup_override_pct",
};

export function slugifyClientName(name: string): string {
  return (
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "client"
  );
}

function parseAmount(grandTotal: string | undefined): number {
  return parseFloat((grandTotal ?? "").replace(/[^0-9.-]/g, "")) || 0;
}

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) throw new Error("Not signed in");
  return data.user.id;
}

/** Raw `clients` rows, no document-derived merge — used by getClients() and the full-account export (backlog LAUNCH-9). */
export async function getClientRows(): Promise<ClientRow[]> {
  const { data, error } = await supabase
    .from("clients")
    .select(
      "id, user_id, name, contact_name, email, phone, city, address, notes, term, retainage_pct, tax_override_pct, markup_override_pct, created_at, updated_at",
    );
  if (error) throw error;
  return (data ?? []) as ClientRow[];
}

function rowToSummary(row: ClientRow): Omit<ClientSummary, "outstanding" | "openDocs" | "totalDocs" | "docs"> {
  return {
    id: row.id,
    name: row.name,
    contactName: row.contact_name,
    email: row.email,
    phone: row.phone,
    city: row.city,
    address: row.address,
    notes: row.notes,
    term: row.term,
    retainagePct: row.retainage_pct,
    taxOverridePct: row.tax_override_pct,
    markupOverridePct: row.markup_override_pct,
    isReal: true,
  };
}

/** Every client with at least one document or a real record, aggregated from real document data. */
export async function getClients(preloadedDocItems?: DocumentListItem[]): Promise<ClientSummary[]> {
  const [rows, docItems] = await Promise.all([getClientRows(), preloadedDocItems ?? getAllDocuments()]);

  const realById = new Map(rows.map((row) => [row.id, row]));
  const realBySlug = new Map<string, ClientRow>();
  for (const row of rows) {
    const slug = slugifyClientName(row.name);
    if (!realBySlug.has(slug)) realBySlug.set(slug, row);
  }

  const realDocs = new Map<string, DocumentListItem[]>(rows.map((row) => [row.id, []]));
  const legacy = new Map<string, ClientSummary>();

  for (const item of docItems) {
    const name = item.doc.clientName?.trim();
    const matched =
      (item.doc.clientId && realById.get(item.doc.clientId)) || (name && realBySlug.get(slugifyClientName(name)));

    if (matched) {
      realDocs.get(matched.id)!.push(item);
      continue;
    }

    if (!name) continue;
    const slug = slugifyClientName(name);
    let client = legacy.get(slug);
    if (!client) {
      client = {
        id: slug,
        name,
        contactName: "",
        email: "",
        phone: "",
        city: "",
        address: "",
        notes: "",
        term: "net30",
        retainagePct: 0,
        taxOverridePct: null,
        markupOverridePct: null,
        outstanding: 0,
        openDocs: 0,
        totalDocs: 0,
        docs: [],
        isReal: false,
      };
      legacy.set(slug, client);
    }
    // getAllDocuments() is sorted by updatedAt desc, so the first non-empty
    // value seen per field is the most recently entered one.
    if (!client.email && item.doc.clientEmail) client.email = item.doc.clientEmail;
    if (!client.phone && item.doc.clientPhone) client.phone = item.doc.clientPhone;
    if (!client.address && item.doc.clientAddress) client.address = item.doc.clientAddress;
    client.totalDocs += 1;
    if (isOpenStatus(item.doc.status)) {
      client.openDocs += 1;
      client.outstanding += parseAmount(item.doc.grandTotal);
    }
    client.docs.push(item);
  }

  const real: ClientSummary[] = rows.map((row) => {
    const docs = realDocs.get(row.id) ?? [];
    let outstanding = 0;
    let openDocs = 0;
    for (const item of docs) {
      if (isOpenStatus(item.doc.status)) {
        openDocs += 1;
        outstanding += parseAmount(item.doc.grandTotal);
      }
    }
    return { ...rowToSummary(row), outstanding, openDocs, totalDocs: docs.length, docs };
  });

  return [...real, ...legacy.values()].sort((a, b) => b.outstanding - a.outstanding);
}

export async function getClient(clientId: string): Promise<ClientSummary | null> {
  const clients = await getClients();
  return clients.find((c) => c.id === clientId) ?? null;
}

/** Creates a real client record; returns its new id. */
export async function createClient(draft: ClientDraft): Promise<string> {
  const [user_id, account_id] = await Promise.all([requireUserId(), requireAccountId()]);
  const { data, error } = await supabase
    .from("clients")
    .insert({
      user_id,
      account_id,
      name: draft.name.trim(),
      contact_name: draft.contactName.trim(),
      email: draft.email.trim(),
      phone: draft.phone.trim(),
      city: draft.city.trim(),
      address: draft.address.trim(),
      notes: draft.notes.trim(),
      term: draft.term,
      retainage_pct: draft.retainagePct,
      sample: draft.sample ?? false,
    })
    .select("id")
    .single();
  if (error) throw error;
  return data.id as string;
}

/** Updates one or more editable fields on a real client row (backlog J16 — inline editing). */
export async function updateClient(clientId: string, patch: ClientPatch): Promise<void> {
  const update: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(patch) as [ClientEditableField, string | number | undefined][]) {
    if (value === undefined) continue;
    update[FIELD_COLUMN[key]] = typeof value === "string" ? value.trim() : value;
  }
  if (Object.keys(update).length === 0) return;
  const { error } = await supabase.from("clients").update(update).eq("id", clientId);
  if (error) throw error;
}

/** Deletes every client previously created by sampleData.ts's loadSampleData(), and nothing else. */
export async function deleteSampleClients(): Promise<void> {
  const { error } = await supabase.from("clients").delete().eq("sample", true);
  if (error) throw error;
}

/** A client with open receivables — the Reports "Collections Call List" (backlog H4 v2). */
export interface ClientReceivable {
  id: string;
  name: string;
  contactName: string;
  email: string;
  phone: string;
  /** Open G702/G703 balance. */
  amount: number;
  /** The portion of `amount` that's past due. */
  overdue: number;
  maxDaysLate: number;
  worstBucket: AgingBucketKey | null;
  /** Open G702/G703 document count. */
  docCount: number;
}

/**
 * Who owes money right now, sorted worst-first — built on `getClients()`'s
 * output rather than a fresh query, since every client's documents are
 * already loaded there. Scoped to open receivables only (G702/G703 and
 * Invoice — see `isReceivable`), same definition as the rest of the Reports
 * rebuild; clients with no open receivables are omitted entirely rather
 * than shown at $0, since this list exists specifically to answer "who do I
 * need to call."
 */
export function computeClientReceivables(clients: ClientSummary[]): ClientReceivable[] {
  const rows: ClientReceivable[] = [];
  for (const client of clients) {
    const open = client.docs.filter(({ doc }) => isReceivable(doc) && isOpenStatus(doc.status));
    if (open.length === 0) continue;

    let amount = 0;
    let overdue = 0;
    let maxDaysLate = 0;
    let worstBucket: AgingBucketKey | null = null;
    for (const { doc } of open) {
      const docAmount = parseAmount(doc.grandTotal);
      amount += docAmount;
      const late = daysLate(doc);
      if (late > 0) overdue += docAmount;
      if (late > maxDaysLate) {
        maxDaysLate = late;
        worstBucket = agingBucketOf(doc);
      }
    }
    if (!worstBucket) worstBucket = agingBucketOf(open[0].doc);

    rows.push({
      id: client.id,
      name: client.name,
      contactName: client.contactName,
      email: client.email,
      phone: client.phone,
      amount,
      overdue,
      maxDaysLate,
      worstBucket,
      docCount: open.length,
    });
  }

  return rows.sort((a, b) => b.maxDaysLate - a.maxDaysLate || b.amount - a.amount);
}
